<template>
    <v-dialog v-model="showModal" max-width="520" scroll-strategy="none" @update:model-value="onClose">
        <v-card>
            <v-card-title class="d-flex align-center pa-4" :style="{ backgroundColor: subscriptionStore.statusColor }">
                <v-icon color="white" class="me-2">mdi-credit-card-alert</v-icon>
                <span class="text-white">Payment Attention Required</span>
                <v-spacer/>
                <v-btn icon variant="text" size="small" @click="dismiss">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-4">
                <div class="text-body-1 mb-4">{{ subscriptionStore.statusMessage }}</div>

                <v-list density="compact" class="pa-0">
                    <v-list-item v-if="subscriptionStore.overdueInvoiceCount > 0">
                        <template v-slot:prepend>
                            <v-icon color="red">mdi-file-document-alert</v-icon>
                        </template>
                        <v-list-item-title>
                            {{ subscriptionStore.overdueInvoiceCount }} overdue invoice(s)
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            Total: {{ new Intl.NumberFormat(undefined, {style: 'currency', currency: subscriptionStore.overdueCurrency}).format(subscriptionStore.overdueTotal ?? 0) }}
                        </v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="subscriptionStore.expiredDevicesCount > 0">
                        <template v-slot:prepend>
                            <v-icon color="red">mdi-cellphone-off</v-icon>
                        </template>
                        <v-list-item-title>
                            {{ subscriptionStore.expiredDevicesCount }} expired device subscription(s)
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="subscriptionStore.expiringDevicesCount > 0">
                        <template v-slot:prepend>
                            <v-icon color="amber">mdi-cellphone-clock</v-icon>
                        </template>
                        <v-list-item-title>
                            {{ subscriptionStore.expiringDevicesCount }} device(s) expiring within 30 days
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="subscriptionStore.daysUntilSuspension !== null && subscriptionStore.daysUntilSuspension > 0">
                        <template v-slot:prepend>
                            <v-icon color="deep-orange">mdi-clock-alert</v-icon>
                        </template>
                        <v-list-item-title class="font-weight-bold text-deep-orange">
                            {{ subscriptionStore.daysUntilSuspension }} day(s) until service suspension
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="subscriptionStore.status?.grace_period_end">
                            Suspension date: {{ subscriptionStore.status.grace_period_end }}
                        </v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="subscriptionStore.status?.nearest_invoice_due_date">
                        <template v-slot:prepend>
                            <v-icon color="amber-darken-2">mdi-calendar-alert</v-icon>
                        </template>
                        <v-list-item-title>
                            Earliest invoice due: {{ subscriptionStore.status.nearest_invoice_due_date }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card-text>

            <v-card-actions class="pa-4 pt-0">
                <v-btn variant="text" @click="dismiss">Dismiss</v-btn>
                <v-spacer/>
                <v-btn color="primary" variant="flat" @click="goToInvoices">
                    <v-icon start>mdi-credit-card</v-icon>
                    View Invoices
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {useAuthStore} from "@/stores/authStore";
import {useSubscriptionStore} from "@/stores/subscriptionStore";
import {useNavigation} from "@/composables/globals";

const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const {navigateTo} = useNavigation();
const showModal = ref(false);

const isCompanyAdmin = computed(() => {
    const user = authStore.user;
    return user?.type === 'company' && user?.role?.toLowerCase()?.includes('admin');
});

const shouldShow = computed(() =>
    isCompanyAdmin.value && subscriptionStore.isOverdue && !subscriptionStore.modalDismissed
);

watch(shouldShow, (val) => {
    if (val) showModal.value = true;
}, {immediate: true});

const dismiss = () => {
    subscriptionStore.dismissModal();
    showModal.value = false;
};

const onClose = (val) => {
    if (!val) subscriptionStore.dismissModal();
};

const goToInvoices = () => {
    dismiss();
    navigateTo('invoices');
};
</script>
